
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";

import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { SessionTimeoutProvider } from "@/components/SessionTimeoutProvider";
import ProtectedRoute from "@/components/ProtectedRoute";
import { RoleProtectedRoute } from "@/components/RoleProtectedRoute";
import { CapabilityGate } from "@/components/CapabilityGate";
import { StrategicProtectedRoute } from "@/components/StrategicProtectedRoute";
import { ThemeProvider } from "next-themes";
import { SystemMaintenanceProvider } from "@/components/SystemMaintenanceProvider";
import { Suspense, ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { queryClient } from "@/lib/queryClient";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";

// Páginas leves carregadas diretamente
import AuthPage from "./pages/AuthPage";
import NotFound from "./pages/NotFound";

// God Mode (completamente isolado)
import { GodModeRoutes } from "./godmode/components/GodModeRoutes";

// Dashboard também com lazy loading para reduzir bundle inicial
const Index = lazy(() => import("./pages/Index"));

// Páginas pesadas com lazy loading
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const ProjectViewPage = lazy(() => import("./pages/ProjectViewPage"));
const ProjectEditPage = lazy(() => import("./pages/ProjectEditPage"));
const ContractsPage = lazy(() => import("./pages/ContractsPage"));
const ContractDetailPage = lazy(() => import("./pages/ContractDetailPage"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const TasksPage = lazy(() => import("./pages/TasksPage"));
const BoardInvitationsPage = lazy(() => import("./pages/BoardInvitationsPage"));
const TaskBoardPage = lazy(() => import("./pages/TaskBoardPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const TrashPage = lazy(() => import("./pages/TrashPage"));
const PortfoliosPage = lazy(() => import("./pages/PortfoliosPage"));
const PortfolioDetailPage = lazy(() => import("./pages/PortfolioDetailPage"));

// Novas páginas de configurações separadas
const GeneralSettingsPage = lazy(() => import("./pages/settings/GeneralSettingsPage"));
const ContractingSettingsPage = lazy(() => import("./pages/settings/ContractingSettingsPage"));
// UnitBudgetSettingsPage removida - integrada em GeneralSettingsPage

// Páginas do Módulo Estratégico
const StrategicDashboardPage = lazy(() => import("./pages/strategic/StrategicDashboardPage"));
const BusinessObjectivesPage = lazy(() => import("./pages/strategic/BusinessObjectivesPage"));
const OnePageListPage = lazy(() => import("./pages/strategic/OnePageListPage"));
const OnePageDetailPage = lazy(() => import("./pages/strategic/OnePageDetailPage"));
const StrategicFrontsPage = lazy(() => import("./pages/strategic/StrategicFrontsPage"));
const ConnectionsPage = lazy(() => import("./pages/strategic/ConnectionsPage"));
const StrategicElementsPage = lazy(() => import("./pages/strategic/StrategicElementsPage"));
const ActionPlanKanbanPage = lazy(() => import("./pages/strategic/ActionPlanKanbanPage"));
const StrategicCodeSettingsPage = lazy(() => import("./pages/strategic/StrategicCodeSettingsPage"));
const StrategicStatusSettingsPage = lazy(() => import("./pages/strategic/StrategicStatusSettingsPage"));
const StrategicImportPage = lazy(() => import("./pages/strategic/StrategicImportPage"));
const IndicatorTypesPage = lazy(() => import("./pages/strategic/IndicatorTypesPage"));
const AlertThresholdsPage = lazy(() => import("./pages/settings/AlertThresholdsPage"));
const ReviewEnvironmentPage = lazy(() => import("./pages/strategic/ReviewEnvironmentPage"));

// Placeholders "Em breve" — novos módulos
const DemandsPage = lazy(() => import("./pages/DemandsPage"));
const InternalProjectsPage = lazy(() => import("./pages/InternalProjectsPage"));

const AtenaPage = lazy(() => import("./pages/ai-hub/AiHubPages").then(m => ({ default: m.AtenaPage })));
const AssistedPlanningPage = lazy(() => import("./pages/ai-hub/AiHubPages").then(m => ({ default: m.AssistedPlanningPage })));
const KnowledgeHubPage = lazy(() => import("./pages/ai-hub/AiHubPages").then(m => ({ default: m.KnowledgeHubPage })));

// Loading component para páginas lazy
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[40vh] w-full">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

// Suspense estável: só mostra fallback quando um chunk lazy realmente precisa ser baixado.
// NÃO usar key={location.pathname} aqui — isso forçaria remontagem completa a cada navegação,
// causando "flash" de tela inteira entre rotas.
const RouteRenderer = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);


const AppRoutes = () => (
  <RouteRenderer>
    <Routes>
      <Route path="/auth" element={<AuthPage />} />
      
      {/* Dashboard - apenas ADMIN/SUPERVISOR */}
      <Route path="/" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <Index />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      
      {/* Projetos - todos podem acessar (RLS filtra dados) */}
      <Route path="/projects" element={<ProtectedRoute><CapabilityGate module="projects"><ProjectsPage /></CapabilityGate></ProtectedRoute>} />
      <Route path="/projects/:id" element={<ProtectedRoute><CapabilityGate module="projects"><ProjectViewPage /></CapabilityGate></ProtectedRoute>} />
      <Route path="/projects/:id/edit" element={<ProtectedRoute><CapabilityGate module="projects"><ProjectEditPage /></CapabilityGate></ProtectedRoute>} />
      
      {/* Contratos - todos podem acessar (RLS filtra dados) */}
      <Route path="/contracts" element={<ProtectedRoute><CapabilityGate module="contracts"><ContractsPage /></CapabilityGate></ProtectedRoute>} />
      <Route path="/contracts/:id" element={<ProtectedRoute><CapabilityGate module="contracts"><ContractDetailPage /></CapabilityGate></ProtectedRoute>} />
      
      {/* Calendário - ADMIN/SUPERVISOR/PADRAO (filtrado por participação) */}
      <Route path="/calendar" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR', 'PADRAO']} redirectTo="/projects">
            <CapabilityGate module="calendar"><CalendarPage /></CapabilityGate>
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      
      {/* Central de Tarefas - ADMIN/SUPERVISOR/PADRAO (VISUALIZACAO bloqueado) */}
      <Route path="/tasks" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR', 'PADRAO']} redirectTo="/projects">
            <CapabilityGate module="tasks"><TasksPage /></CapabilityGate>
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/tasks/invitations" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR', 'PADRAO']} redirectTo="/projects">
            <CapabilityGate module="tasks"><BoardInvitationsPage /></CapabilityGate>
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/tasks/board/:boardId" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR', 'PADRAO']} redirectTo="/projects">
            <CapabilityGate module="tasks"><TaskBoardPage /></CapabilityGate>
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />


      {/* Módulo Gestão Estratégica - ADMIN/SUPERVISOR ou usuários com participação */}
      <Route path="/strategic/dashboard" element={
        <ProtectedRoute>
          <StrategicProtectedRoute>
            <StrategicDashboardPage />
          </StrategicProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/strategic/objectives" element={
        <ProtectedRoute>
          <StrategicProtectedRoute>
            <BusinessObjectivesPage />
          </StrategicProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/strategic/onepages" element={
        <ProtectedRoute>
          <StrategicProtectedRoute>
            <OnePageListPage />
          </StrategicProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/strategic/onepages/:id" element={
        <ProtectedRoute>
          <StrategicProtectedRoute>
            <OnePageDetailPage />
          </StrategicProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/strategic/onepages/:planId/action-plan/:actionPlanId" element={
        <ProtectedRoute>
          <StrategicProtectedRoute>
            <ActionPlanKanbanPage />
          </StrategicProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/strategic/fronts" element={
        <ProtectedRoute>
          <StrategicProtectedRoute>
            <StrategicFrontsPage />
          </StrategicProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/strategic/connections" element={
        <ProtectedRoute>
          <StrategicProtectedRoute>
            <ConnectionsPage />
          </StrategicProtectedRoute>
        </ProtectedRoute>
      } />
      
      {/* Rotas de configuração estratégica - apenas ADMIN/SUPERVISOR */}
      <Route path="/strategic/import" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <StrategicImportPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/strategic/elements" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <StrategicElementsPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/strategic/codes" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <StrategicCodeSettingsPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/strategic/indicator-types" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <IndicatorTypesPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/strategic/statuses" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <StrategicStatusSettingsPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/strategic/alert-thresholds" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <AlertThresholdsPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/strategic/review/:planId" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <ReviewEnvironmentPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      
      {/* Relatórios - apenas ADMIN/SUPERVISOR */}
      <Route path="/reports" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <ReportsPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/reports/:tab" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <ReportsPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/reports/:tab/:subtab" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <ReportsPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      
      {/* Configurações Gerais - apenas ADMIN/SUPERVISOR */}
      <Route path="/settings/general" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <GeneralSettingsPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      
      {/* Configurações de Contratações - apenas ADMIN/SUPERVISOR */}
      <Route path="/settings/contracting" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <ContractingSettingsPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      
      {/* Redirect para orçamento setorial (antiga rota) */}
      <Route path="/settings/unit-budget" element={<Navigate to="/settings/general?tab=budget" replace />} />
      
      {/* Preferências pessoais - todos podem acessar */}
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      
      {/* Redirects de compatibilidade para /admin */}
      <Route path="/admin" element={<Navigate to="/settings/general" replace />} />
      <Route path="/admin/:tab" element={<Navigate to="/settings/general" replace />} />
      <Route path="/budget-settings" element={<Navigate to="/settings/contracting?tab=budget" replace />} />
      
      {/* Lixeira - apenas ADMIN/SUPERVISOR */}
      <Route path="/trash" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <TrashPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      
      {/* Portfólios - apenas ADMIN/SUPERVISOR */}
      <Route path="/portfolios" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <PortfoliosPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      <Route path="/portfolios/:id" element={
        <ProtectedRoute>
          <RoleProtectedRoute allowedRoles={['ADMIN', 'SUPERVISOR']} redirectTo="/projects">
            <PortfolioDetailPage />
          </RoleProtectedRoute>
        </ProtectedRoute>
      } />
      
      {/* God Mode Routes - Isolado do sistema principal */}
      <Route path="/godmode/*" element={<GodModeRoutes />} />
      
      {/* Placeholders "Em breve" */}
      <Route path="/demands" element={<ProtectedRoute><DemandsPage /></ProtectedRoute>} />
      <Route path="/internal-projects" element={<ProtectedRoute><InternalProjectsPage /></ProtectedRoute>} />
      
      <Route path="/ai-hub/atena" element={<ProtectedRoute><AtenaPage /></ProtectedRoute>} />
      <Route path="/ai-hub/assisted-planning" element={<ProtectedRoute><AssistedPlanningPage /></ProtectedRoute>} />
      <Route path="/ai-hub/knowledge" element={<ProtectedRoute><KnowledgeHubPage /></ProtectedRoute>} />

      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  </RouteRenderer>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <AuthProvider>
        <OrganizationProvider>
          <SystemMaintenanceProvider>
            <SessionTimeoutProvider>
              <BrowserRouter>
                <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
                  <AppRoutes />
                </div>
                <Toaster />
                <Sonner />
              </BrowserRouter>
            </SessionTimeoutProvider>
          </SystemMaintenanceProvider>
        </OrganizationProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
